const SINGLE_MIN_OFFSET = 16;
const SINGLE_MAX_OFFSET = 36;
const RECIPROCAL_MIN_OFFSET = 32;
const RECIPROCAL_MAX_OFFSET = 56;
const RECIPROCAL_LANE_OFFSET = 7;
const ROUTE_SAMPLE_COUNT = 24;
const EDGE_OBSTACLE_SEARCH_MARGIN = 128;

export const EDGE_ROUTING_MODES = Object.freeze({
  AUTO: "auto",
  STRAIGHT: "straight",
  GENTLE_LEFT: "gentle-left",
  GENTLE_RIGHT: "gentle-right",
  STRONG_LEFT: "strong-left",
  STRONG_RIGHT: "strong-right",
});

const VALID_EDGE_ROUTING_MODES = new Set(Object.values(EDGE_ROUTING_MODES));
const topologyCache = new WeakMap();

export function normalizeEdgeRoutingMode(value) {
  return VALID_EDGE_ROUTING_MODES.has(value)
    ? value
    : EDGE_ROUTING_MODES.AUTO;
}

export function getEdgeTopology({ edges = [], nodeCount = 0, sourceId, targetId }) {
  let topologyStats = topologyCache.get(edges);
  if (!topologyStats) {
    const degrees = new Map();
    for (const edge of edges) {
      const source = String(edge.source);
      const target = String(edge.target);
      degrees.set(source, (degrees.get(source) || 0) + 1);
      degrees.set(target, (degrees.get(target) || 0) + 1);
    }
    const degreeValues = Array.from(degrees.values()).sort((a, b) => a - b);
    topologyStats = {
      degrees,
      medianDegree: degreeValues.length
        ? degreeValues[Math.floor(degreeValues.length / 2)]
        : 0,
    };
    topologyCache.set(edges, topologyStats);
  }

  const { degrees, medianDegree } = topologyStats;
  const sourceDegree = degrees.get(String(sourceId)) || 0;
  const targetDegree = degrees.get(String(targetId)) || 0;
  const hubThreshold = Math.max(
    6,
    Math.ceil(Math.max(nodeCount, degrees.size) * 0.22),
    medianDegree * 2
  );
  const sourceIsHub = sourceDegree >= hubThreshold;
  const targetIsHub = targetDegree >= hubThreshold;

  return {
    isHubEdge: sourceIsHub || targetIsHub,
    // Reversing the sign for an incoming hub edge keeps the visual fan rotating
    // in the same direction around the hub.
    hubBendSign: sourceIsHub && !targetIsHub ? 1 : targetIsHub ? -1 : 1,
  };
}

export function getNodeRect(node) {
  if (!node) return null;
  const width = Number(node.width || node.measured?.width || 0);
  const height = Number(node.height || node.measured?.height || 0);
  if (!width || !height) return null;
  const position =
    node.positionAbsolute ||
    node.position ||
    (Number.isFinite(node.x) && Number.isFinite(node.y)
      ? { x: node.x, y: node.y }
      : { x: 0, y: 0 });
  return { x: position.x, y: position.y, width, height };
}

export function getRelevantObstacleRects(
  obstacleRects = [],
  { sourceX, sourceY, targetX, targetY, margin = EDGE_OBSTACLE_SEARCH_MARGIN } = {}
) {
  const coordinates = [sourceX, sourceY, targetX, targetY].map(Number);
  if (!coordinates.every(Number.isFinite)) return obstacleRects;
  const [startX, startY, endX, endY] = coordinates;
  const left = Math.min(startX, endX) - margin;
  const right = Math.max(startX, endX) + margin;
  const top = Math.min(startY, endY) - margin;
  const bottom = Math.max(startY, endY) + margin;
  return obstacleRects.filter((rect) => (
    rect.x + rect.width >= left &&
    rect.x <= right &&
    rect.y + rect.height >= top &&
    rect.y <= bottom
  ));
}

export function getNodeGeometrySignature(nodeInternals) {
  if (!nodeInternals?.entries) return "";
  return Array.from(nodeInternals.entries())
    .map(([nodeId, node]) => {
      const rect = getNodeRect(node);
      if (!rect) return `${nodeId}:unmeasured`;
      return `${nodeId}:${rect.x},${rect.y},${rect.width},${rect.height}`;
    })
    .sort()
    .join("|");
}

function boundaryPoint(rect, toward) {
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  const dx = toward.x - centerX;
  const dy = toward.y - centerY;
  const halfWidth = rect.width / 2;
  const halfHeight = rect.height / 2;

  if (!dx && !dy) {
    return {
      x: centerX,
      y: rect.y,
      tangentX: 1,
      tangentY: 0,
    };
  }

  const horizontalRatio = Math.abs(dx) / Math.max(1, halfWidth);
  const verticalRatio = Math.abs(dy) / Math.max(1, halfHeight);
  const scale = 1 / Math.max(horizontalRatio, verticalRatio);
  const hitsVerticalSide = horizontalRatio >= verticalRatio;

  return {
    x: centerX + dx * scale,
    y: centerY + dy * scale,
    tangentX: hitsVerticalSide ? 0 : 1,
    tangentY: hitsVerticalSide ? 1 : 0,
  };
}

export function getFloatingEdgeEndpoints({
  sourceNode,
  targetNode,
  sourceId,
  targetId,
  reciprocal = false,
}) {
  const sourceRect = getNodeRect(sourceNode);
  const targetRect = getNodeRect(targetNode);
  if (!sourceRect || !targetRect) return null;

  const sourceCenter = {
    x: sourceRect.x + sourceRect.width / 2,
    y: sourceRect.y + sourceRect.height / 2,
  };
  const targetCenter = {
    x: targetRect.x + targetRect.width / 2,
    y: targetRect.y + targetRect.height / 2,
  };

  if (String(sourceId) === String(targetId)) {
    return {
      sourceX: sourceCenter.x,
      sourceY: sourceRect.y + sourceRect.height,
      targetX: targetCenter.x,
      targetY: targetRect.y,
    };
  }

  const sourcePoint = boundaryPoint(sourceRect, targetCenter);
  const targetPoint = boundaryPoint(targetRect, sourceCenter);
  const laneSign =
    reciprocal && String(sourceId).localeCompare(String(targetId)) > 0 ? -1 : 1;
  const laneOffset = reciprocal ? RECIPROCAL_LANE_OFFSET * laneSign : 0;

  return {
    sourceX: sourcePoint.x + sourcePoint.tangentX * laneOffset,
    sourceY: sourcePoint.y + sourcePoint.tangentY * laneOffset,
    targetX: targetPoint.x + targetPoint.tangentX * laneOffset,
    targetY: targetPoint.y + targetPoint.tangentY * laneOffset,
  };
}

export function getCustomEdgeGeometry({
  sourceX,
  sourceY,
  targetX,
  targetY,
  reciprocal = false,
  bendSign = 1,
  bendScale = 1,
}) {
  const curveStrength = 0.35;
  const dx = targetX - sourceX;
  const dy = targetY - sourceY;
  const distance = Math.hypot(dx, dy) || 1;
  const reciprocalOffset = Math.min(
    RECIPROCAL_MAX_OFFSET,
    Math.max(RECIPROCAL_MIN_OFFSET, distance * 0.12)
  );
  const singleOffset = Math.min(
    SINGLE_MAX_OFFSET,
    Math.max(SINGLE_MIN_OFFSET, distance * 0.06)
  );
  const normalX = -dy / distance;
  const normalY = dx / distance;
  const curveOffset =
    (reciprocal ? reciprocalOffset : singleOffset * bendScale) * bendSign;
  const controlOffsetX = normalX * curveOffset;
  const controlOffsetY = normalY * curveOffset;
  const controlX1 = sourceX + dx * curveStrength + controlOffsetX;
  const controlY1 = sourceY + dy * curveStrength + controlOffsetY;
  const controlX2 = targetX - dx * curveStrength + controlOffsetX;
  const controlY2 = targetY - dy * curveStrength + controlOffsetY;
  const visibleTargetX = targetX;
  const visibleTargetY = targetY;

  return {
    controlX1,
    controlY1,
    controlX2,
    controlY2,
    visibleTargetX,
    visibleTargetY,
    points: [
      { x: sourceX, y: sourceY },
      { x: controlX1, y: controlY1 },
      { x: controlX2, y: controlY2 },
      { x: visibleTargetX, y: visibleTargetY },
    ],
  };
}

function cubicPoint(points, t) {
  const [p0, p1, p2, p3] = points;
  const mt = 1 - t;
  return {
    x:
      mt ** 3 * p0.x +
      3 * mt ** 2 * t * p1.x +
      3 * mt * t ** 2 * p2.x +
      t ** 3 * p3.x,
    y:
      mt ** 3 * p0.y +
      3 * mt ** 2 * t * p1.y +
      3 * mt * t ** 2 * p2.y +
      t ** 3 * p3.y,
  };
}

function pointToRectDistance(point, rect, expansion = 0) {
  const left = rect.x - expansion;
  const right = rect.x + rect.width + expansion;
  const top = rect.y - expansion;
  const bottom = rect.y + rect.height + expansion;
  const dx = Math.max(left - point.x, 0, point.x - right);
  const dy = Math.max(top - point.y, 0, point.y - bottom);
  return Math.hypot(dx, dy);
}

function routeScore(points, obstacleRects, bendScale, preferredScale) {
  let score = Math.abs(bendScale - preferredScale) * 10;
  for (let sample = 2; sample < ROUTE_SAMPLE_COUNT - 1; sample += 1) {
    const point = cubicPoint(points, sample / ROUTE_SAMPLE_COUNT);
    for (const rect of obstacleRects) {
      const distance = pointToRectDistance(point, rect, 8);
      if (distance === 0) {
        score += 10000;
      } else if (distance < 28) {
        score += (28 - distance) * 24;
      }
    }
  }
  return score;
}

function stableSign(seed) {
  let hash = 0;
  for (const character of String(seed || "")) {
    hash = (hash * 31 + character.charCodeAt(0)) | 0;
  }
  return hash % 2 === 0 ? 1 : -1;
}

export function getAdaptiveEdgeGeometry({
  sourceX,
  sourceY,
  targetX,
  targetY,
  reciprocal = false,
  obstacleRects = [],
  seed = "",
  routingMode = EDGE_ROUTING_MODES.AUTO,
  topology = {},
}) {
  const normalizedRoutingMode = normalizeEdgeRoutingMode(routingMode);
  if (reciprocal) {
    return getCustomEdgeGeometry({
      sourceX,
      sourceY,
      targetX,
      targetY,
      reciprocal: true,
    });
  }

  const manualRoutes = {
    [EDGE_ROUTING_MODES.STRAIGHT]: { bendSign: 1, bendScale: 0 },
    [EDGE_ROUTING_MODES.GENTLE_LEFT]: { bendSign: -1, bendScale: 0.9 },
    [EDGE_ROUTING_MODES.GENTLE_RIGHT]: { bendSign: 1, bendScale: 0.9 },
    [EDGE_ROUTING_MODES.STRONG_LEFT]: { bendSign: -1, bendScale: 1.9 },
    [EDGE_ROUTING_MODES.STRONG_RIGHT]: { bendSign: 1, bendScale: 1.9 },
  };
  const manualRoute = manualRoutes[normalizedRoutingMode];
  if (manualRoute) {
    return getCustomEdgeGeometry({
      sourceX,
      sourceY,
      targetX,
      targetY,
      ...manualRoute,
    });
  }

  const isHubEdge = Boolean(topology.isHubEdge);
  const preferredScale = isHubEdge ? 1 : 0.55;
  const preferredSign = stableSign(seed);
  const candidates = [];
  const bendScales = isHubEdge ? [1, 1.5, 2.2] : [0.55, 1, 1.7, 2.5];
  const topologySign = isHubEdge ? topology.hubBendSign || 1 : preferredSign;
  for (const bendScale of bendScales) {
    for (const bendSign of [topologySign, -topologySign]) {
      const geometry = getCustomEdgeGeometry({
        sourceX,
        sourceY,
        targetX,
        targetY,
        bendSign,
        bendScale,
      });
      candidates.push({
        geometry,
        score:
          routeScore(geometry.points, obstacleRects, bendScale, preferredScale) +
          (bendSign === topologySign ? 0 : isHubEdge ? 14 : 0),
      });
    }
  }

  candidates.sort((first, second) => first.score - second.score);
  return candidates[0].geometry;
}

export function getPointClearance(point, obstacleRects) {
  if (!obstacleRects.length) return 1000000;
  return Math.min(
    ...obstacleRects.map((rect) => pointToRectDistance(point, rect, 6))
  );
}
